// Interactive speech state (zustand vanilla store; React reads it with
// `useStore`). One store per composer. Partials live here and in the
// recording state's read-only live text only; the composer's text receives
// nothing but committed text.
import { createStore, type StoreApi } from 'zustand/vanilla';
import type { SpeechIssue } from '../errors';

export type SpeechPhase =
  | 'idle'
  | 'starting'
  | 'live'
  | 'reconnecting'
  | 'committing'
  | 'kept'
  | 'offline'
  | 'pending-insert';

/** Phases in which the microphone or the link is working; Send stays hidden. */
export const ACTIVE_PHASES: ReadonlySet<SpeechPhase> = new Set([
  'starting',
  'live',
  'reconnecting',
  'offline',
  'committing',
]);

export type CaptionSegment = {
  key: string;
  text: string;
  /** Leading characters that will not change (the whole text for a final). */
  stable: number;
  final: boolean;
  startMs: number;
  endMs: number;
};

export type SpeechNotice = { kind: 'idle-stop' | 'limit'; seconds: number } | null;

export type SpeechState = {
  phase: SpeechPhase;
  segments: CaptionSegment[];
  /** Smoothed 0..1 microphone level. */
  level: number;
  error: SpeechIssue | null;
  /** The spool session kept for Retry or Discard. */
  keptSessionId: string | null;
  /** Committed text held for an explicit Insert (hidden page, lost device). */
  pendingText: string | null;
  degraded: boolean;
  notice: SpeechNotice;
  /** Increments on every successful insert, for the "Inserted" announcement. */
  inserted: number;
};

export const INITIAL_SPEECH_STATE: SpeechState = {
  phase: 'idle',
  segments: [],
  level: 0,
  error: null,
  keptSessionId: null,
  pendingText: null,
  degraded: false,
  notice: null,
  inserted: 0,
};

export type SpeechStore = StoreApi<SpeechState>;

/** A new store; `initial` overrides the idle state (server rendering and tests read it). */
export const createSpeechStore = (initial: Partial<SpeechState> = {}): SpeechStore =>
  createStore<SpeechState>(() => ({ ...INITIAL_SPEECH_STATE, ...initial }));

/** Replace a partial or add a final, keeping capture order. */
export function upsertSegment(segments: CaptionSegment[], next: CaptionSegment): CaptionSegment[] {
  const rest = segments.filter((segment) => segment.key !== next.key);
  return [...rest, next].sort((a, b) => Number(b.final) - Number(a.final) || a.startMs - b.startMs);
}
